/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

class Burndown extends React.Component {
  constructor() {
    super();
    this.chart = null;

    this.generateGraph = this.generateGraph.bind(this);
  }

  componentDidMount() {
    if (this.props.graphData) {
      this.generateGraph();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.graphData !== this.props.graphData ||
      prevProps.width !== this.props.width ||
      prevProps.height !== this.props.height
    ) {
      this.generateGraph();
    }
  }

  numTicksForWidth = (width) => Math.round(width / 40);

  generateGraph() {
    const { width, cadences = [], childrenPoints = 0 } = this.props;
    const data = this.props.graphData || {};
    const dates = Object.keys(data);
    const xTicks = parseInt(width / 40, 10);
    const xAxis = ['x'];
    const values = Object.values(data);
    const indexesWithAddedToCadence = {};

    const targetLine = ['ideal target line'];
    const burnLine = ['burn line'];
    const trendLine = ['actual trend line'];
    const addedToCadence = ['added to cadence'];
    const maxPoints =
      values.length > 0
        ? Math.round(values[0].target_burn_down * 100) / 100
        : 5;
    for (let i = 0; i < values.length; i += 1) {
      const value = values[i];
      const currentDate = dates[i];
      const isFuture = moment(currentDate).isAfter(moment());
      const burnLineValue = Math.round(value.remaining_estimate * 100) / 100;
      xAxis.push(dates[i]);
      targetLine.push(Math.round(value.target_burn_down * 100) / 100);
      if (value.trend_burn_down != null)
        trendLine.push(Math.round(value.trend_burn_down * 100) / 100);
      if (isFuture) {
        // eslint-disable-next-line
        continue;
      }
      burnLine.push(burnLineValue);
      if (value.added_to_cadences && value.added_to_cadences.length > 0) {
        // Store which cadence was story was added to
        indexesWithAddedToCadence[
          burnLine.length - 1
        ] = value.added_to_cadences.map((cadenceId) => {
          const cadence = cadences.find((c) => c.id === Number(cadenceId));
          return cadence ? cadence.short_name : cadenceId;
        });

        // Repeat all other values
        xAxis.push(dates[i]);
        targetLine.push(Math.round(value.target_burn_down * 100) / 100);
        if (value.trend_burn_down != null)
          trendLine.push(Math.round(value.trend_burn_down * 100) / 100);
        burnLine.push(burnLineValue);

        // Add different values for add to cadence in order to create the
        // vertical line
        addedToCadence.push(0);
        addedToCadence.push(maxPoints);
      } else {
        addedToCadence.push(null);
      }
    }

    // eslint-disable-next-line

    this.chart = c3.generate({
      bindto: `.chart-${this.props.workPackageId}`,
      size: {
        width: this.props.width,
        height: this.props.height
      },
      data: {
        x: 'x',
        columns: trendLine.length > 1 ? [xAxis, targetLine, trendLine, burnLine, addedToCadence] : [xAxis, targetLine, burnLine, addedToCadence],
        colors: trendLine.length > 1 ? {
          'actual trend line': '#DE70BC',
          'ideal target line': '#6F6E6F',
          'burn line': '#703F64',
          'added to cadence': '#276997'
        } : 
        {
          'actual trend line': '#DE70BC',
          'burn line': '#703F64',
          'added to cadence': '#276997'
        } 

      },
      axis: {
        x: {
          type: 'timeseries',
          tick: {
            count: dates.length > xTicks ? xTicks : dates.length,
            format: '%b %d'
          }
        },
        y: {
          min: 0,
          padding: { bottom: 0 },
          label: {
            text: '# of hours',
            position: 'outer-middle'
          }
        },
        y2: {
          show: childrenPoints > 0, // this determines when the third axis will show on chart
          min: 0,
          max: childrenPoints + 1,
          label: {
            text: '# of points',
            position: 'outer-middle'
          }
        }
      },
      tooltip: {
        format: {
          value(value, ratio, id, index) {
            if (indexesWithAddedToCadence[index]) {
              if (id === 'added to cadence') {
                return indexesWithAddedToCadence[index].join();
              }
              return undefined;
            }
            if (id === 'added to cadence') {
              return undefined;
            }
            return value;
          }
        }
      },
      legend: {
        show: false
      }
    });

    this.props.setBurndownChart(this.chart);
  }

  render() {
    return (
      <div
        id="chart"
        className={`burndown-chart chart-${this.props.workPackageId}`}
      />
    );
  }
}

Burndown.propTypes = {
  graphData: PropTypes.object
};

export default Burndown;
